// turnkey-signer-bridge.ts - routes NON-CUSTODIAL Turnkey embedded-wallet signing
// through the Bounded-owned signer page (auth.bounded.sh/wallet/turnkey/signer).
//
// TRANSPORT: by default the signer runs inside the in-app WIDGET (turnkey-wallet-
// widget.ts) - an iframe behind a Shadow-DOM modal that matches the Bounded widget
// look + motion, so signing feels native. Environments where the frame cannot
// mount fall back to a real popup window; both transports speak the same
// postMessage protocol.
//
// Turnkey wallets produce raw detached signatures. The signer runs the Turnkey
// browser SDK on the single Bounded origin and stamps an ed25519 signRawPayload
// with the wallet's 24-hour email-established signing session (an emailed one-time
// code is the sole signing authority; within a live session every signature is one
// approval click). The app retains the transaction itself.
//
// ONE ORIGIN, ONE SESSION: the signing session lives in the signer origin's own
// storage, out of every app's reach, so ALL Bounded apps (any subdomain / custom
// domain) sign through this one origin - giving every user one wallet that roams
// across every app.
import { getActiveSessionManager, getConfig } from '@bounded-sh/core';
import bs58 from 'bs58';
import { Buffer } from 'buffer';
import { getPlatform } from '../platform';
import { runWidget } from './turnkey-wallet-widget';
import { openHiddenSignerFrame } from './turnkey-signer-frame';
import { requestTurnkeySigningCapability, buildTurnkeySignRequest, requestTurnkeyActionAuthorization, buildTurnkeyActionRequest, } from './turnkey-signing-capability';
function hasDOM() {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
}
// Dispatch one signer exchange through the in-app WIDGET, degrading to the POPUP
// when we cannot embed (no DOM, or the frame never mounts/loads). Nothing in the
// email-session flow needs a popup by preference: the code entry and the approval
// click both run fine inside a cross-origin iframe on every engine (the old
// Safari popup preference existed only for in-iframe WebAuthn CREATE).
async function deliver(url, origin, ex) {
    const popup = () => runPopup(url, origin, ex);
    return runWidget(url, origin, { request: ex.request, onResult: ex.onResult }, popup);
}
async function turnkeySignerPage() {
    const cfg = await getConfig();
    const base = (cfg.humanAuthApiUrl || 'https://auth.bounded.sh').replace(/\/$/, '');
    return { url: `${base}/wallet/turnkey/signer`, origin: new URL(base).origin };
}
function toHex(bytes) {
    let h = '';
    for (const b of bytes)
        h += b.toString(16).padStart(2, '0');
    return h;
}
function fromHex(hex) {
    const clean = hex.startsWith('0x') ? hex.slice(2) : hex;
    const out = new Uint8Array(clean.length / 2);
    for (let i = 0; i < out.length; i++)
        out[i] = parseInt(clean.substr(i * 2, 2), 16);
    return out;
}
function decodeJwt(token) {
    try {
        let value = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const padding = value.length % 4;
        if (padding === 2)
            value += '==';
        else if (padding === 3)
            value += '=';
        return JSON.parse(getPlatform().atob(value));
    }
    catch (_a) {
        return {};
    }
}
function activeTurnkeyAddress() {
    let token = null;
    try {
        token = getActiveSessionManager().getIdToken();
    }
    catch (_a) {
        token = null;
    }
    if (!token)
        return null;
    const claims = decodeJwt(token);
    const address = claims['custom:walletAddress'];
    const provider = claims['custom:walletProvider'];
    return provider === 'turnkey' && typeof address === 'string' && address
        ? address
        : null;
}
let requestSeq = 0;
// Shared popup driver: open the signer page, do the ready/postMessage handshake, and
// resolve when `onResult` accepts a result message. Origin + source are pinned.
async function runPopup(url, signerOrigin, ex) {
    var _a, _b;
    const w = (_a = ex.width) !== null && _a !== void 0 ? _a : 420, h = (_b = ex.height) !== null && _b !== void 0 ? _b : 620;
    const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
    const popup = window.open(url, 'bounded-turnkey-signer', `width=${w},height=${h},left=${left},top=${top}`);
    if (!popup) {
        throw new Error('Popup blocked - call this from a user gesture (e.g. a click handler) so the wallet approval window can open.');
    }
    return await new Promise((resolve, reject) => {
        let settled = false;
        // DS3-0412: the signer's `bounded:turnkey:ready` is the authoritative readiness signal, NOT
        // a bare `postMessage`. On a slow navigation the popup can still be at about:blank when the
        // 1.2s fallback fires, so a post to `signerOrigin` is silently dropped (target-origin mismatch).
        // We therefore never latch "sent" on a post that returned. We (re)send the request on every
        // `ready`, keep the 1.2s kick only as a fallback for signer pages that omit `ready`, and stop
        // resending once the signer acknowledges THIS requestId (the terminal `result` is that ack; a
        // future signer may also emit `bounded:turnkey:ack`) or the exchange settles.
        let readySeen = false;
        let acked = false;
        let closeTimer;
        const cleanup = () => { settled = true; window.removeEventListener('message', onMsg); if (closeTimer)
            clearInterval(closeTimer); };
        const sendRequest = () => { if (settled || acked)
            return; try {
            popup.postMessage(ex.request, signerOrigin);
        }
        catch ( /* popup gone */_a) { /* popup gone */ } };
        const onMsg = (event) => {
            if (event.origin !== signerOrigin)
                return;
            if (event.source !== popup)
                return;
            const d = event.data;
            if (!d || typeof d !== 'object')
                return;
            if (d.type === 'bounded:turnkey:ready') {
                readySeen = true;
                sendRequest();
                return;
            }
            if (d.type === 'bounded:turnkey:ack' && d.requestId === ex.request.requestId) {
                acked = true;
                return;
            }
            if (d.type === 'bounded:turnkey:result' && d.requestId === ex.request.requestId) {
                const outcome = ex.onResult(d);
                if ('error' in outcome) {
                    cleanup();
                    try {
                        popup.close();
                    }
                    catch ( /* ignore */_a) { /* ignore */ }
                    reject(new Error(outcome.error));
                    return;
                }
                if (outcome.done) {
                    cleanup();
                    try {
                        popup.close();
                    }
                    catch ( /* ignore */_b) { /* ignore */ }
                    resolve(outcome.value);
                }
            }
        };
        window.addEventListener('message', onMsg);
        // Fallback only for signer pages that never emit `ready`; suppressed once `ready` (the reliable
        // path) has driven a send or the signer has acked, so a slow-nav drop can no longer strand the op.
        setTimeout(() => { if (!readySeen && !acked)
            sendRequest(); }, 1200);
        closeTimer = setInterval(() => {
            if (popup.closed) {
                clearInterval(closeTimer);
                setTimeout(() => { if (!settled) {
                    cleanup();
                    reject(new Error('Wallet approval window was closed before it completed.'));
                } }, 400);
            }
        }, 500);
        setTimeout(() => { if (!settled) {
            cleanup();
            try {
                popup.close();
            }
            catch ( /* ignore */_a) { /* ignore */ }
            reject(new Error('Wallet approval timed out.'));
        } }, 5 * 60 * 1000);
    });
}
// AS-02: the hosted signer refuses to sign unless the request carries an issuer-signed
// SIGNING CAPABILITY bound to the app id, this origin, and the EXACT bytes (a bare postMessage
// from any peer can no longer consent-phish a signature). Fetch one from the human-auth issuer
// for exactly these bytes before posting the sign request. Credential-free: the caller proves
// identity with the Bounded id token WE minted (a body secret, RS256-verified server-side), not
// an ambient cookie - the issuer's session cookie is SameSite-scoped to its own origin and is
// not sent on this cross-origin fetch, so the token is what carries the session's wallet.
async function fetchSigningCapability(messageHex) {
    const cfg = await getConfig();
    const base = (cfg.humanAuthApiUrl || 'https://auth.bounded.sh').replace(/\/$/, '');
    let idToken = null;
    try {
        idToken = getActiveSessionManager().getIdToken();
    }
    catch (_a) {
        idToken = null;
    }
    if (!idToken) {
        throw new Error('This session has no Turnkey wallet. Sign in again to provision one.');
    }
    return requestTurnkeySigningCapability({
        baseUrl: base,
        idToken,
        messageHex,
        fetchImpl: (url, init) => fetch(url, init),
    });
}
// DS3-0510: wallet reads and creation disclose a stable wallet identity, so the hosted signer
// requires an issuer-signed authorization bound to this app origin and exact action. Fetch it
// before posting to the signer. Walletless sessions are valid for createWallet because that is
// the operation that provisions their first wallet.
async function fetchActionAuthorization(action) {
    const cfg = await getConfig();
    const base = (cfg.humanAuthApiUrl || 'https://auth.bounded.sh').replace(/\/$/, '');
    let idToken = null;
    try {
        idToken = getActiveSessionManager().getIdToken();
    }
    catch (_a) {
        idToken = null;
    }
    if (!idToken) {
        throw new Error('Sign in again to set up your Turnkey wallet.');
    }
    return requestTurnkeyActionAuthorization({
        baseUrl: base,
        idToken,
        action,
        fetchImpl: (url, init) => fetch(url, init),
    });
}
/**
 * Destroy the 24h signing session held on the signer origin for this device.
 * Called by logout(): logout must END the signing session, never leave it to
 * expire (TURNKEY-EMAIL-SIGNING-PLAN.md). Best-effort by design - the session
 * lives in the signer origin's storage, so the only way to reach it is a hidden
 * signer iframe, and a blocked/offline frame must never block logout itself;
 * an unswept session is account-bound and unusable without a live authorization,
 * it just costs its owner nothing and a stranger nothing.
 */
export async function clearTurnkeySigningSession() {
    if (!hasDOM())
        return;
    // The session can live in TWO places, and the sweep has to reach both:
    //   1. the signer origin's store, shared by every signer frame in this app's partition -
    //      cleared by posting into a throwaway frame below;
    //   2. the WARM widget iframe's memory. The signer keeps an in-memory mirror when the
    //      browser blocks its storage, and hideModal deliberately leaves that iframe mounted
    //      between signatures, so the mirror outlives logout. A throwaway frame cannot see
    //      another document's memory - the only way to take it is to destroy that document.
    // Done first, and synchronously, so it lands even if the frame exchange below fails.
    try {
        const { origin } = await turnkeySignerPage();
        (await import('./bounded-widget-modal')).detachWarmSignerIframe(origin);
    }
    catch (_a) {
        /* no modal module / no warm frame: nothing to take */
    }
    let frame = null;
    try {
        frame = await openHiddenSignerFrame();
        await frame.request({ type: 'bounded:turnkey:clearSigningSession', requestId: `tkclear_${Date.now()}_${++requestSeq}` }, 4000);
    }
    catch (_b) {
        /* best-effort: never block or fail logout on the sweep */
    }
    finally {
        frame === null || frame === void 0 ? void 0 : frame.dispose();
    }
}
export function hasTurnkeyWallet() {
    return activeTurnkeyAddress() !== null;
}
/**
 * Get-or-create the user's non-custodial Turnkey wallet, resolving with its stable
 * Solana address. Creation never prompts: on first use the signer silently provisions
 * the email-rooted wallet (no code), and an existing wallet resolves immediately.
 * Call from a USER GESTURE (the transport may need to open a window).
 *
 * Use this to learn the fee-payer address BEFORE building a transaction to sign.
 */
export async function getOrCreateTurnkeyWallet() {
    if (!hasDOM())
        throw new Error('Turnkey embedded wallets are only available in the browser (popup-based).');
    const { url, origin } = await turnkeySignerPage();
    const requestId = `tkcreate_${Date.now()}_${++requestSeq}`;
    const authorization = await fetchActionAuthorization('createWallet');
    return await deliver(url, origin, {
        request: buildTurnkeyActionRequest({ requestId, action: 'createWallet', authorization }),
        onResult: (d) => {
            if (d.ok && typeof d.address === 'string')
                return { done: true, value: { address: d.address, subOrgId: String(d.subOrgId || '') } };
            return { error: String(d.error || 'Wallet setup failed') };
        },
    });
}
/**
 * Open the hosted PRIVATE-KEY EXPORT page for the session's Turnkey wallet in a
 * new tab. Works for BOTH session kinds: hosted (issuer-cookie) sessions open it
 * bare, and inline (authMode:"turnkey") sessions - which own no issuer cookie -
 * carry a short-lived, purpose-bound exportWallet authorization in the URL
 * FRAGMENT (never sent to any server; the page strips it from the address bar
 * immediately and uses it as its bearer session). MUST be called from a USER
 * GESTURE or the browser blocks the tab. The key itself is decrypted only in
 * that tab - it never transits Bounded.
 */
export async function openTurnkeyKeyExport() {
    if (!hasDOM())
        throw new Error('Turnkey key export is only available in the browser.');
    if (!hasTurnkeyWallet())
        throw new Error('This session has no Turnkey wallet to export.');
    // Open the tab SYNCHRONOUSLY - before any await - so the click's transient user
    // activation is not consumed by the mint below; a window.open issued after an
    // awaited round trip is refused by strict popup blockers even from a click
    // handler. Same hard-won pattern as onramp() and loginWithPopup. Note the
    // features string must NOT contain `noopener`: per HTML, window.open returns
    // null when noopener is set, so the handle needed to navigate the tab (and to
    // tell "blocked" from "opened") would be unavailable. Severing the back-link
    // with `handle.opener = null` gives the same protection and keeps the handle.
    const handle = window.open('', '_blank');
    if (!handle) {
        throw new Error('Popup blocked - call openTurnkeyKeyExport() from a user gesture (e.g. a click handler).');
    }
    try {
        handle.opener = null;
    }
    catch ( /* older engines: the tab still cannot read us cross-origin */_a) { /* older engines: the tab still cannot read us cross-origin */ }
    try {
        const cfg = await getConfig();
        const base = (cfg.humanAuthApiUrl || 'https://auth.bounded.sh').replace(/\/$/, '');
        const authorization = await fetchActionAuthorization('exportWallet');
        handle.location.href = `${base}/wallet/turnkey/export#authorization=${encodeURIComponent(authorization)}`;
    }
    catch (error) {
        // Never strand a blank tab on a failed mint.
        try {
            handle.close();
        }
        catch ( /* already gone */_b) { /* already gone */ }
        throw error;
    }
}
/**
 * Open the Bounded Turnkey signer popup and sign `message` (raw bytes) with the
 * user's non-custodial Turnkey wallet, resolving with the ed25519 signature +
 * the wallet address. MUST be called from a USER GESTURE (click/tap) or the
 * browser blocks the popup.
 *
 * The signer shows the decoded transaction on an approve card. With a live 24-hour
 * signing session (normally established by the login code itself) one click signs;
 * without one, the card collects a Turnkey-emailed one-time code inline, which
 * establishes the session, and the same click completes the signature.
 */
export async function signSolanaMessageViaTurnkey(message) {
    if (!hasDOM()) {
        throw new Error('Turnkey embedded-wallet signing is only available in the browser (popup-based).');
    }
    const { url, origin } = await turnkeySignerPage();
    const requestId = `tksign_${Date.now()}_${++requestSeq}`;
    const messageHex = toHex(message);
    // AS-02: obtain the issuer-signed capability for THESE exact bytes first; the signer will
    // refuse (capability_required) without it. Fetched per signature (each is single-payload).
    const capability = await fetchSigningCapability(messageHex);
    // Inline (authMode:"turnkey") sessions plant no Better Auth cookie on the issuer, so the
    // signer's same-origin status/create fetches need a bearer session proof: attach the
    // createWallet action authorization (it covers the sign flow's get-or-create too). Minted
    // from the same id token as the capability, so a session that minted one can mint both.
    const authorization = await fetchActionAuthorization('createWallet');
    return await deliver(url, origin, {
        request: buildTurnkeySignRequest({ requestId, messageHex, capability, authorization }),
        onResult: (d) => {
            if (d.ok && typeof d.signatureHex === 'string' && typeof d.address === 'string') {
                return { done: true, value: { signature: fromHex(d.signatureHex), address: d.address, subOrgId: String(d.subOrgId || '') } };
            }
            return { error: String(d.error || 'Turnkey signing failed') };
        },
    });
}
function requireMatchingAddress(result) {
    const expected = activeTurnkeyAddress();
    if (!expected)
        throw new Error('This session has no Turnkey wallet. Sign in again to provision one.');
    if (result.address !== expected)
        throw new Error('Turnkey signer returned a different wallet address than the active session.');
    return expected;
}
export async function signMessageWithTurnkey(message) {
    const result = await signSolanaMessageViaTurnkey(getPlatform().textEncode(message));
    requireMatchingAddress(result);
    return bs58.encode(result.signature);
}
export async function signTransactionWithTurnkey(transaction) {
    const isLegacy = 'serializeMessage' in transaction;
    const message = isLegacy
        ? transaction.serializeMessage()
        : transaction.message.serialize();
    const result = await signSolanaMessageViaTurnkey(new Uint8Array(message));
    const address = requireMatchingAddress(result);
    const { PublicKey } = await import('@solana/web3.js');
    if (isLegacy) {
        transaction.addSignature(new PublicKey(address), Buffer.from(result.signature));
    }
    else {
        transaction.addSignature(new PublicKey(address), result.signature);
    }
    return transaction;
}
// DS3-0414: does the transaction already carry a REAL signer signature? If so, its compiled
// message (recentBlockhash included) must not be mutated - see signAndSubmitTransactionWithTurnkey.
// Legacy signatures are `{ signature: Buffer | null }` entries; versioned signatures are 64-byte
// arrays that stay all-zero placeholders until a signer fills them.
function transactionHasSignature(transaction) {
    if ('serializeMessage' in transaction) {
        return transaction.signatures.some((s) => s.signature != null);
    }
    return transaction.signatures.some((sig) => sig.some((b) => b !== 0));
}
export async function signAndSubmitTransactionWithTurnkey(transaction, feePayer) {
    var _a;
    const config = await getConfig();
    const rpcUrl = (_a = config.rpcUrl) === null || _a === void 0 ? void 0 : _a.trim();
    if (!rpcUrl)
        throw new Error('Turnkey transaction submission requires init({ rpcUrl }).');
    const { Connection, PublicKey } = await import('@solana/web3.js');
    const connection = new Connection(rpcUrl, 'confirmed');
    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
    const isLegacy = 'serializeMessage' in transaction;
    // DS3-0414: a Solana signature is computed over the compiled message, which INCLUDES
    // recentBlockhash. Rewriting the blockhash after any signer (sponsor / multisig / co-signer)
    // has signed silently invalidates their signature - signTransactionWithTurnkey only ADDS the
    // Turnkey signature, it never re-signs the others. So only refresh the blockhash while the
    // transaction is still unsigned; once a real signature is present, preserve the caller's message
    // verbatim and confirm against the blockhash it already carries.
    const hasSignature = transactionHasSignature(transaction);
    let confirmBlockhash = blockhash;
    if (!hasSignature) {
        if (isLegacy) {
            const legacy = transaction;
            legacy.recentBlockhash = blockhash;
            legacy.lastValidBlockHeight = lastValidBlockHeight;
            if (!legacy.feePayer) {
                const address = activeTurnkeyAddress();
                if (!address)
                    throw new Error('This session has no Turnkey wallet. Sign in again to provision one.');
                legacy.feePayer = feePayer !== null && feePayer !== void 0 ? feePayer : new PublicKey(address);
            }
        }
        else {
            transaction.message.recentBlockhash = blockhash;
        }
    }
    else {
        confirmBlockhash = (isLegacy
            ? transaction.recentBlockhash
            : transaction.message.recentBlockhash) || blockhash;
    }
    const signed = await signTransactionWithTurnkey(transaction);
    const signature = await connection.sendRawTransaction(signed.serialize(), {
        skipPreflight: false,
        maxRetries: 3,
    });
    const confirmation = await connection.confirmTransaction({ signature, blockhash: confirmBlockhash, lastValidBlockHeight }, 'confirmed');
    if (confirmation.value.err) {
        throw new Error(`Turnkey transaction failed: ${JSON.stringify(confirmation.value.err)}`);
    }
    return signature;
}
